import { Settings, Plus, Trash2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { BODY_COLORS, PLINTH_HEIGHTS, GLASS_TYPES, PROFILE_COLORS, PROFILE_TYPES, LEAD_TIMES, DEFAULT_PAYMENT_TERMS, PROJECT_NOTE_MAX_LENGTH } from '../constants';

export default function GlobalConfigPanel() {
  const {
    config,
    setConfig,
    MODELS,
    getAvailableMaterialsForIndex,
    getAvailableColorsForIndex,
    readOnlyState,
    proposalType,
    units,
    activeUnitId,
    projectMeta,
    setProjectMeta,
  } = useAppContext();

  const activeUnitLabel = proposalType === 'bulk' ? units.find(u => u.id === activeUnitId)?.label : undefined;

  // Calculate total combinations count (at least 1 for primary config)
  const extraCount = Math.max(
    (config.extraModels || []).length,
    (config.extraMaterials || []).length,
    (config.extraDoorColors || []).length
  );

  const combinations = [
    {
      model: config.model,
      customModel: config.customModel,
      material: config.material,
      customMaterial: config.customMaterial,
      doorColor: config.doorColor,
      customDoorColor: config.customDoorColor,
    },
    ...Array.from({ length: extraCount }).map((_, i) => ({
      model: config.extraModels?.[i] || '',
      customModel: '',
      material: config.extraMaterials?.[i] || '',
      customMaterial: '',
      doorColor: config.extraDoorColors?.[i] || '',
      customDoorColor: '',
    }))
  ];

  const handleAddCombination = () => {
    setConfig({
      ...config,
      extraModels: [...(config.extraModels || []), ''],
      extraMaterials: [...(config.extraMaterials || []), ''],
      extraDoorColors: [...(config.extraDoorColors || []), ''],
    });
  };

  const handleRemoveCombination = (idx: number) => {
    if (idx === 0) return;
    const extraIdx = idx - 1;
    setConfig({
      ...config,
      extraModels: (config.extraModels || []).filter((_, i) => i !== extraIdx),
      extraMaterials: (config.extraMaterials || []).filter((_, i) => i !== extraIdx),
      extraDoorColors: (config.extraDoorColors || []).filter((_, i) => i !== extraIdx),
    });
  };

  const handleUpdateModel = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, model: val });
    } else {
      const updated = [...(config.extraModels || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraModels: updated });
    }
  };

  const handleUpdateCustomModel = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, customModel: val });
    }
  };

  const handleUpdateMaterial = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, material: val });
    } else {
      const updated = [...(config.extraMaterials || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraMaterials: updated });
    }
  };

  const handleUpdateCustomMaterial = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, customMaterial: val });
    }
  };

  const handleUpdateDoorColor = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, doorColor: val });
    } else {
      const updated = [...(config.extraDoorColors || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraDoorColors: updated });
    }
  };

  const handleUpdateCustomDoorColor = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, customDoorColor: val });
    }
  };

  // Gövde Rengi: primary slot + optional extra slots. Some projects mix two body
  // colors (ör. ada antrasit, duvar üstü beyaz); each picked color gets its own
  // "Gövde Rengi" görsel kartı automatically (AppContext'teki senkron effect).
  const bodyColorSlots = [config.bodyColor, ...(config.extraBodyColors || [])];

  const getAvailableBodyColorsForSlot = (idx: number) =>
    BODY_COLORS.filter(c => c === bodyColorSlots[idx] || !bodyColorSlots.includes(c));

  const canAddBodyColor =
    !readOnlyState.isReadOnly &&
    bodyColorSlots.every(Boolean) &&
    bodyColorSlots.length < BODY_COLORS.length;

  const handleUpdateBodyColor = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, bodyColor: val });
    } else {
      const updated = [...(config.extraBodyColors || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraBodyColors: updated });
    }
  };

  const handleAddBodyColor = () => {
    setConfig({ ...config, extraBodyColors: [...(config.extraBodyColors || []), ''] });
  };

  const handleRemoveBodyColor = (idx: number) => {
    if (idx === 0) return;
    setConfig({
      ...config,
      extraBodyColors: (config.extraBodyColors || []).filter((_, i) => i !== idx - 1),
    });
  };

  // Cam Tipi ve Profil Rengi: Gövde Rengi ile aynı çoklu-slot deseni — bazı
  // projelerde birden fazla cam tipi / profil rengi bir arada kullanılıyor.
  const glassTypeSlots = [config.glassType, ...(config.extraGlassTypes || [])];

  const getAvailableGlassTypesForSlot = (idx: number) =>
    GLASS_TYPES.filter(g => g === glassTypeSlots[idx] || !glassTypeSlots.includes(g));

  const canAddGlassType =
    !readOnlyState.isReadOnly &&
    glassTypeSlots.every(Boolean) &&
    glassTypeSlots.length < GLASS_TYPES.length;

  const handleUpdateGlassType = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, glassType: val });
    } else {
      const updated = [...(config.extraGlassTypes || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraGlassTypes: updated });
    }
  };

  const handleAddGlassType = () => {
    setConfig({ ...config, extraGlassTypes: [...(config.extraGlassTypes || []), ''] });
  };

  const handleRemoveGlassType = (idx: number) => {
    if (idx === 0) return;
    setConfig({
      ...config,
      extraGlassTypes: (config.extraGlassTypes || []).filter((_, i) => i !== idx - 1),
    });
  };

  const profileColorSlots = [config.profileColor, ...(config.extraProfileColors || [])];

  const getAvailableProfileColorsForSlot = (idx: number) =>
    PROFILE_COLORS.filter(p => p === profileColorSlots[idx] || !profileColorSlots.includes(p));

  const canAddProfileColor =
    !readOnlyState.isReadOnly &&
    profileColorSlots.every(Boolean) &&
    profileColorSlots.length < PROFILE_COLORS.length;

  const handleUpdateProfileColor = (idx: number, val: string) => {
    if (idx === 0) {
      setConfig({ ...config, profileColor: val });
    } else {
      const updated = [...(config.extraProfileColors || [])];
      updated[idx - 1] = val;
      setConfig({ ...config, extraProfileColors: updated });
    }
  };

  const handleAddProfileColor = () => {
    setConfig({ ...config, extraProfileColors: [...(config.extraProfileColors || []), ''] });
  };

  const handleRemoveProfileColor = (idx: number) => {
    if (idx === 0) return;
    setConfig({
      ...config,
      extraProfileColors: (config.extraProfileColors || []).filter((_, i) => i !== idx - 1),
    });
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-stone-200/80 shadow-card space-y-5">
      <div className="flex items-center justify-between border-b border-stone-100 pb-3">
        <div className="flex items-center space-x-2 text-stone-800 font-semibold">
          <Settings className="w-4 h-4 text-brand-600" />
          <span className="text-xs uppercase tracking-wider font-semibold text-stone-700">Mutfak Konfigürasyonu</span>
        </div>
        {activeUnitLabel && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50/80 border border-brand-200/60 px-2.5 py-0.5 rounded-full">
            {activeUnitLabel}
          </span>
        )}
      </div>

      {/* Combination Cards Section */}
      <div className="space-y-3">
        {combinations.map((comb, idx) => (
          <div 
            key={idx} 
            className="bg-stone-50/60 p-4 rounded-xl border border-stone-200/70 space-y-3 relative transition-all"
          >
            <div className="flex items-center justify-between border-b border-stone-200/50 pb-2">
              <div className="flex items-center space-x-2">
                <span className="w-5 h-5 rounded-full bg-brand-100/80 text-brand-700 text-[10px] font-semibold flex items-center justify-center">
                  {idx + 1}
                </span>
                <span className="text-xs font-semibold text-stone-800 tracking-tight">
                  {idx === 0 ? '1. Kombinasyon' : `${idx + 1}. Kombinasyon`}
                </span>
              </div>
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => handleRemoveCombination(idx)}
                  className="text-stone-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors flex items-center space-x-1 text-[10px] font-medium"
                  title="Bu kombinasyonu kaldır"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Kaldır</span>
                </button>
              )}
            </div>

            <div className="space-y-3">
              {/* Row 1: Model & Material side by side */}
              <div className="grid grid-cols-2 gap-2.5">
                {/* Mutfak Modeli */}
                <div>
                  <label className="text-[11px] font-medium text-stone-500 block mb-1">Mutfak Modeli</label>
                  <select
                    value={comb.model}
                    onChange={e => handleUpdateModel(idx, e.target.value)}
                    className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                      comb.model === '' ? 'text-stone-400 italic font-normal' : 'text-stone-800 font-medium'
                    }`}
                  >
                    <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                    {MODELS.map(m => (
                      <option key={m} value={m} className="text-stone-800 font-medium not-italic">
                        {m === 'CUSTOM' ? 'Özel Giriş...' : m}
                      </option>
                    ))}
                  </select>
                  {comb.model === 'CUSTOM' && idx === 0 && (
                    <input 
                      type="text" 
                      placeholder="Model girin..."
                      value={config.customModel}
                      onChange={e => handleUpdateCustomModel(0, e.target.value)}
                      className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white text-stone-800 transition-all"
                    />
                  )}
                </div>

                {/* Kapak Malzemesi */}
                <div>
                  <label className="text-[11px] font-medium text-stone-500 block mb-1">Kapak Malzemesi</label>
                  <select
                    value={comb.material}
                    onChange={e => handleUpdateMaterial(idx, e.target.value)}
                    className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                      comb.material === '' ? 'text-stone-400 italic font-normal' : 'text-stone-800 font-medium'
                    }`}
                  >
                    <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                    {getAvailableMaterialsForIndex(idx).map(m => (
                      <option key={m} value={m} className="text-stone-800 font-medium not-italic">
                        {m === 'CUSTOM' ? 'Özel Giriş...' : m}
                      </option>
                    ))}
                  </select>
                  {comb.material === 'CUSTOM' && idx === 0 && (
                    <input 
                      type="text" 
                      placeholder="Malzeme girin..."
                      value={config.customMaterial}
                      onChange={e => handleUpdateCustomMaterial(0, e.target.value)}
                      className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white text-stone-800 transition-all"
                    />
                  )}
                </div>
              </div>

              {/* Row 2: Kapak Rengi */}
              <div>
                <label className="text-[11px] font-medium text-stone-500 block mb-1">Kapak Rengi</label>
                <select
                  value={comb.doorColor}
                  onChange={e => handleUpdateDoorColor(idx, e.target.value)}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                    comb.doorColor === '' ? 'text-stone-400 italic font-normal' : 'text-stone-800 font-medium'
                  }`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                  {getAvailableColorsForIndex(idx).map(color => (
                    <option key={color} value={color} className="text-stone-800 font-medium not-italic">
                      {color}
                    </option>
                  ))}
                  <option value="CUSTOM" className="text-stone-800 font-medium not-italic">Özel Giriş...</option>
                </select>
                {comb.doorColor === 'CUSTOM' && idx === 0 && (
                  <input 
                    type="text" 
                    placeholder="Özel kapak rengi girin..."
                    value={config.customDoorColor}
                    onChange={e => handleUpdateCustomDoorColor(0, e.target.value)}
                    className="w-full mt-1.5 px-3 py-1.5 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white text-stone-800 transition-all"
                  />
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Add Combination Button */}
        <button
          type="button"
          onClick={handleAddCombination}
          className="w-full py-2 bg-brand-50/80 hover:bg-brand-100 text-brand-700 border border-brand-200/60 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all"
        >
          <Plus className="w-3.5 h-3.5 text-brand-600" />
          <span>Kombinasyon Ekle</span>
        </button>
      </div>
      
      {/* Other Global Options */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-stone-100">
        <div>
          <label className="text-[11px] font-medium text-stone-500">Gövde Rengi</label>
          <div className="space-y-1.5 mt-1">
            {bodyColorSlots.map((slotValue, idx) => (
              <div key={idx} className="flex items-center space-x-1.5">
                <select
                  value={slotValue}
                  disabled={readOnlyState.isReadOnly}
                  onChange={e => handleUpdateBodyColor(idx, e.target.value)}
                  className={`flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                    slotValue === ''
                      ? 'text-stone-400 italic font-normal'
                      : 'text-stone-800 font-medium'
                  } disabled:bg-stone-50 disabled:cursor-not-allowed`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                  {getAvailableBodyColorsForSlot(idx).map(c => (
                    <option key={c} value={c} className="text-stone-800 font-medium not-italic">
                      {c}
                    </option>
                  ))}
                </select>
                {idx > 0 && !readOnlyState.isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveBodyColor(idx)}
                    title="Gövde rengini kaldır"
                    className="text-stone-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {canAddBodyColor && (
              <button
                type="button"
                onClick={handleAddBodyColor}
                className="w-full py-1 text-[11px] font-semibold text-brand-700 bg-brand-50/80 hover:bg-brand-100 border border-brand-200/60 rounded-lg flex items-center justify-center space-x-1 transition-all"
              >
                <Plus className="w-3 h-3 text-brand-600" />
                <span>Gövde Rengi Ekle</span>
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-stone-500">Baza Yüksekliği</label>
          <select
            value={config.plinthHeight}
            disabled={readOnlyState.isReadOnly}
            onChange={e => setConfig({ ...config, plinthHeight: e.target.value })}
            className={`w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
              config.plinthHeight === '' 
                ? 'text-stone-400 italic font-normal' 
                : 'text-stone-800 font-medium'
            } disabled:bg-stone-50 disabled:cursor-not-allowed`}
          >
            <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
            {PLINTH_HEIGHTS.map(h => (
              <option key={h} value={h} className="text-stone-800 font-medium not-italic">
                {h}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-medium text-stone-500">Cam Tipi</label>
          <div className="space-y-1.5 mt-1">
            {glassTypeSlots.map((slotValue, idx) => (
              <div key={idx} className="flex items-center space-x-1.5">
                <select
                  value={slotValue}
                  disabled={readOnlyState.isReadOnly}
                  onChange={e => handleUpdateGlassType(idx, e.target.value)}
                  className={`flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                    slotValue === ''
                      ? 'text-stone-400 italic font-normal'
                      : 'text-stone-800 font-medium'
                  } disabled:bg-stone-50 disabled:cursor-not-allowed`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                  {getAvailableGlassTypesForSlot(idx).map(g => (
                    <option key={g} value={g} className="text-stone-800 font-medium not-italic">
                      {g}
                    </option>
                  ))}
                </select>
                {idx > 0 && !readOnlyState.isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveGlassType(idx)}
                    title="Cam tipini kaldır"
                    className="text-stone-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {canAddGlassType && (
              <button
                type="button"
                onClick={handleAddGlassType}
                className="w-full py-1 text-[11px] font-semibold text-brand-700 bg-brand-50/80 hover:bg-brand-100 border border-brand-200/60 rounded-lg flex items-center justify-center space-x-1 transition-all"
              >
                <Plus className="w-3 h-3 text-brand-600" />
                <span>Cam Tipi Ekle</span>
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-stone-500">Profil Rengi</label>
          <div className="space-y-1.5 mt-1">
            {profileColorSlots.map((slotValue, idx) => (
              <div key={idx} className="flex items-center space-x-1.5">
                <select
                  value={slotValue}
                  disabled={readOnlyState.isReadOnly}
                  onChange={e => handleUpdateProfileColor(idx, e.target.value)}
                  className={`flex-1 min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
                    slotValue === ''
                      ? 'text-stone-400 italic font-normal'
                      : 'text-stone-800 font-medium'
                  } disabled:bg-stone-50 disabled:cursor-not-allowed`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
                  {getAvailableProfileColorsForSlot(idx).map(p => (
                    <option key={p} value={p} className="text-stone-800 font-medium not-italic">
                      {p}
                    </option>
                  ))}
                </select>
                {idx > 0 && !readOnlyState.isReadOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemoveProfileColor(idx)}
                    title="Profil rengini kaldır"
                    className="text-stone-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {canAddProfileColor && (
              <button
                type="button"
                onClick={handleAddProfileColor}
                className="w-full py-1 text-[11px] font-semibold text-brand-700 bg-brand-50/80 hover:bg-brand-100 border border-brand-200/60 rounded-lg flex items-center justify-center space-x-1 transition-all"
              >
                <Plus className="w-3 h-3 text-brand-600" />
                <span>Profil Rengi Ekle</span>
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-medium text-stone-500">Profil Tipi</label>
          <select
            value={config.profileType || ''}
            disabled={readOnlyState.isReadOnly}
            onChange={e => setConfig({ ...config, profileType: e.target.value })}
            className={`w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
              (config.profileType || '') === ''
                ? 'text-stone-400 italic font-normal'
                : 'text-stone-800 font-medium'
            } disabled:bg-stone-50 disabled:cursor-not-allowed`}
          >
            <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
            {PROFILE_TYPES.map(t => (
              <option key={t} value={t} className="text-stone-800 font-medium not-italic">
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[11px] font-medium text-stone-500">Termin Süresi</label>
          <select
            value={config.leadTime}
            onChange={e => setConfig({ ...config, leadTime: e.target.value })}
            className={`w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 transition-all ${
              config.leadTime === '' 
                ? 'text-stone-400 italic font-normal' 
                : 'text-stone-800 font-medium'
            }`}
          >
            <option value="" className="text-stone-400 italic font-normal">Lütfen seçiniz</option>
            {LEAD_TIMES.map(t => (
              <option key={t} value={t} className="text-stone-800 font-medium not-italic">
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="pt-3 border-t border-stone-100">
        <label className="text-[11px] font-medium text-stone-500">
          Proje Notu {activeUnitLabel ? `(${activeUnitLabel})` : ''}
        </label>
        <textarea
          value={config.projectNote || ''}
          disabled={readOnlyState.isReadOnly}
          maxLength={PROJECT_NOTE_MAX_LENGTH}
          onChange={e => setConfig({ ...config, projectNote: e.target.value })}
          placeholder="Teklif PDF'sinin Proje Özeti sayfasında görünmesini istediğiniz kısa bir not girin (opsiyonel)"
          rows={3}
          className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 text-stone-800 font-medium placeholder:text-stone-400 placeholder:font-normal disabled:bg-stone-50 disabled:cursor-not-allowed transition-all resize-none"
        />
        <div className="mt-1 text-right text-[10px] text-stone-400">
          {(config.projectNote || '').length}/{PROJECT_NOTE_MAX_LENGTH}
        </div>
      </div>

      {proposalType === 'bulk' && (
        <div className="space-y-3 pt-3 border-t border-stone-100">
          <div className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider">
            Proje Geneli Teslimat Tarihleri (Ticari Koşullar'da kullanılır)
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium text-stone-500">Ödeme Şekli</label>
              <input
                type="text"
                disabled={readOnlyState.isReadOnly}
                value={projectMeta.paymentTerms ?? ''}
                onChange={e => setProjectMeta({ ...projectMeta, paymentTerms: e.target.value })}
                placeholder={DEFAULT_PAYMENT_TERMS}
                className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 text-stone-800 font-medium placeholder:text-stone-400 placeholder:font-normal disabled:bg-stone-50 disabled:cursor-not-allowed transition-all"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-stone-500">Son Sipariş Tarihi</label>
              <input
                type="date"
                disabled={readOnlyState.isReadOnly}
                value={projectMeta.orderDeadline || ''}
                onChange={e => setProjectMeta({ ...projectMeta, orderDeadline: e.target.value })}
                className="w-full mt-1 px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 text-stone-800 font-medium disabled:bg-stone-50 disabled:cursor-not-allowed transition-all"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-stone-500">Final Teslim Tarihi (Müşteri, Ay/Yıl)</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <select
                  disabled={readOnlyState.isReadOnly}
                  value={projectMeta.finalDeliveryDeadline?.split('-')[1] || ''}
                  onChange={e => {
                    const year = projectMeta.finalDeliveryDeadline?.split('-')[0] || String(new Date().getFullYear());
                    setProjectMeta({ ...projectMeta, finalDeliveryDeadline: e.target.value ? `${year}-${e.target.value}` : '' });
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 disabled:bg-stone-50 disabled:cursor-not-allowed transition-all ${
                    projectMeta.finalDeliveryDeadline?.split('-')[1] ? 'text-stone-800 font-medium' : 'text-stone-400 italic font-normal'
                  }`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Ay seçiniz</option>
                  {[
                    ['01', 'Ocak'], ['02', 'Şubat'], ['03', 'Mart'], ['04', 'Nisan'], ['05', 'Mayıs'], ['06', 'Haziran'],
                    ['07', 'Temmuz'], ['08', 'Ağustos'], ['09', 'Eylül'], ['10', 'Ekim'], ['11', 'Kasım'], ['12', 'Aralık'],
                  ].map(([value, label]) => (
                    <option key={value} value={value} className="text-stone-800 font-medium not-italic">{label}</option>
                  ))}
                </select>
                <select
                  disabled={readOnlyState.isReadOnly}
                  value={projectMeta.finalDeliveryDeadline?.split('-')[0] || ''}
                  onChange={e => {
                    const month = projectMeta.finalDeliveryDeadline?.split('-')[1] || '01';
                    setProjectMeta({ ...projectMeta, finalDeliveryDeadline: e.target.value ? `${e.target.value}-${month}` : '' });
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 disabled:bg-stone-50 disabled:cursor-not-allowed transition-all ${
                    projectMeta.finalDeliveryDeadline?.split('-')[0] ? 'text-stone-800 font-medium' : 'text-stone-400 italic font-normal'
                  }`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Yıl seçiniz</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
                    <option key={year} value={String(year)} className="text-stone-800 font-medium not-italic">{year}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-medium text-stone-500">Ürün Teslim Tarihi (Ay/Yıl)</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <select
                  disabled={readOnlyState.isReadOnly}
                  value={projectMeta.deliveryMonth?.split('-')[1] || ''}
                  onChange={e => {
                    const year = projectMeta.deliveryMonth?.split('-')[0] || String(new Date().getFullYear());
                    setProjectMeta({ ...projectMeta, deliveryMonth: e.target.value ? `${year}-${e.target.value}` : '' });
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 disabled:bg-stone-50 disabled:cursor-not-allowed transition-all ${
                    projectMeta.deliveryMonth?.split('-')[1] ? 'text-stone-800 font-medium' : 'text-stone-400 italic font-normal'
                  }`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Ay seçiniz</option>
                  {[
                    ['01', 'Ocak'], ['02', 'Şubat'], ['03', 'Mart'], ['04', 'Nisan'], ['05', 'Mayıs'], ['06', 'Haziran'],
                    ['07', 'Temmuz'], ['08', 'Ağustos'], ['09', 'Eylül'], ['10', 'Ekim'], ['11', 'Kasım'], ['12', 'Aralık'],
                  ].map(([value, label]) => (
                    <option key={value} value={value} className="text-stone-800 font-medium not-italic">{label}</option>
                  ))}
                </select>
                <select
                  disabled={readOnlyState.isReadOnly}
                  value={projectMeta.deliveryMonth?.split('-')[0] || ''}
                  onChange={e => {
                    const month = projectMeta.deliveryMonth?.split('-')[1] || '01';
                    setProjectMeta({ ...projectMeta, deliveryMonth: e.target.value ? `${e.target.value}-${month}` : '' });
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border border-stone-200/90 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 disabled:bg-stone-50 disabled:cursor-not-allowed transition-all ${
                    projectMeta.deliveryMonth?.split('-')[0] ? 'text-stone-800 font-medium' : 'text-stone-400 italic font-normal'
                  }`}
                >
                  <option value="" className="text-stone-400 italic font-normal">Yıl seçiniz</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
                    <option key={year} value={String(year)} className="text-stone-800 font-medium not-italic">{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
